import {
  ATTENTION_STATES,
  RECONCILING_STATES,
  type RowConfig,
  type RowSeverity,
} from '../service-administration/rowConfig';
import type { Deployment } from '../../types/backend';

const ATTENTION = new Set(ATTENTION_STATES);
const RECONCILING = new Set(RECONCILING_STATES);

/** Higher = more severe. Used for worst-of rollup across a cell. */
export const SEVERITY_RANK: Record<RowSeverity, number> = {
  err: 4,
  warn: 3,
  info: 2,
  ok: 1,
  muted: 0,
};

export interface CellCounts {
  total: number;
  inSync: number;
  attention: number;
  reconciling: number;
  acknowledged: number;
  unsubscribed: number;
  /** Deployments flagged blocked (ERROR/TAINTED) — overlay indicator. */
  blocked: number;
  /** Deployments whose desired state has not yet been written to the spoke. */
  pendingSync: number;
}

export interface CellSummary extends CellCounts {
  accountId: string;
  region: string;
  /** Worst row severity in the cell; 'muted' when the cell only has unsubscribed/acknowledged rows. */
  severity: RowSeverity;
}

function worstSeverity(severities: RowSeverity[]): RowSeverity {
  return severities.reduce<RowSeverity>(
    (worst, s) => (SEVERITY_RANK[s] > SEVERITY_RANK[worst] ? s : worst),
    'muted',
  );
}

/**
 * Roll up the rows of a single (account, region) cell into one summary.
 * `rows[i]` is the RowConfig for `deployments[i]` (same order, same length).
 */
export function summarizeCell(
  accountId: string,
  region: string,
  deployments: Deployment[],
  rows: RowConfig[],
): CellSummary {
  const counts: CellCounts = {
    total: deployments.length,
    inSync: 0,
    attention: 0,
    reconciling: 0,
    acknowledged: 0,
    unsubscribed: 0,
    blocked: 0,
    pendingSync: 0,
  };

  rows.forEach((cfg, i) => {
    const d = deployments[i];
    if (cfg.state === 'in-sync') counts.inSync += 1;
    if (cfg.state === 'acknowledged-drift') counts.acknowledged += 1;
    if (cfg.state === 'unsubscribed') counts.unsubscribed += 1;
    if (ATTENTION.has(cfg.state)) counts.attention += 1;
    if (RECONCILING.has(cfg.state)) counts.reconciling += 1;
    if (d.blocked) counts.blocked += 1;
    if (d.desired_state_sync_status === 'pending') counts.pendingSync += 1;
  });

  return {
    accountId,
    region,
    ...counts,
    severity: worstSeverity(rows.map((r) => r.severity)),
  };
}

/** Theme color key for a cell's worst severity. */
export function severityColor(
  severity: RowSeverity,
): 'error' | 'warning' | 'info' | 'success' | 'default' {
  switch (severity) {
    case 'err': return 'error';
    case 'warn': return 'warning';
    case 'info': return 'info';
    case 'ok': return 'success';
    case 'muted': return 'default';
  }
}

/** Compact relative age ("47s ago", "3m ago", "2h ago", "4d ago") for heartbeat-grade timestamps. */
export function formatAge(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

/** One-line summary of what's worth noting in the cell, for a tooltip / sub-label. */
export function cellHeadline(cell: CellSummary): string {
  if (cell.total === 0) return 'No deployments';
  const parts: string[] = [];
  if (cell.attention > 0) parts.push(`${cell.attention} need action`);
  if (cell.blocked > 0) parts.push(`${cell.blocked} blocked`);
  if (cell.reconciling > 0) parts.push(`${cell.reconciling} reconciling`);
  if (cell.pendingSync > 0) parts.push(`${cell.pendingSync} pending sync`);
  if (parts.length === 0) {
    return cell.inSync === cell.total ? 'All in sync' : `${cell.inSync}/${cell.total} in sync`;
  }
  return parts.join(' · ');
}
